import mapKeys from 'lodash/mapKeys';
import camelCase from 'lodash/camelCase';
import merge from 'lodash/merge';
import dayjs from 'dayjs';
import log from 'chameleon/ui-stack/utilities/log';
import { getFinishedPercent } from '../components/learnosity/utils/helpers';

/**
 * From https://stackoverflow.com/a/41716722 and https://stackoverflow.com/a/48764436
 * @param {*} num
 * @param {*} decimalPlaces
 */
export const round = (num, decimalPlaces = 0) => {
  const p = 10 ** decimalPlaces;
  const m = num * p * (1 + Number.EPSILON);
  return Math.round(m) / p;
};

export class AllAssignments {
  constructor(assessments = [], assessmentId = null) {
    const matchingAssessment = assessments.find(
      (assessment) => +assessment.id === +assessmentId,
    );

    if (matchingAssessment) {
      const { assignments } = matchingAssessment;
      this.assignments = assignments;
      this.assessment = matchingAssessment;
    } else {
      this.assignments = [];
    }
  }

  getAssignments = () => this.assignments;

  getOpenAssignments = () =>
    this.assignments.filter((assignee) => {
      let isAssignmentComplete = false;
      const { progress } = assignee;
      if (progress) {
        const finishedPercentage = getFinishedPercent(progress);
        isAssignmentComplete = finishedPercentage === 100;
      }
      return assignee.status !== 'closed' && !isAssignmentComplete;
    });
}

export const getSubGroupStudentsLabel = (subGroupStudents) => {
  const { length: subGroupStudentsLength } = subGroupStudents || [];

  if (subGroupStudentsLength === 0) {
    return '';
  }

  if (subGroupStudentsLength === 1) {
    const { firstName, lastName } = mapKeys(subGroupStudents[0], (_, key) =>
      camelCase(key),
    );
    return firstName && lastName
      ? ` (${firstName} ${lastName})`
      : ` (${subGroupStudents[0]})`;
  }

  return ` (${subGroupStudents.length} students)`;
};

/**
 * Handler for getting the western timezone of all the sections.
 * @param {Array} list - The list of timezones (strings)
 * @return {String} - The minimum timezone from a list of timezones.
 * */
export const getMinTimezone = (list = []) => {
  let minTimezone = null;

  list.forEach((timezone) => {
    if (timezone) {
      try {
        if (!minTimezone) {
          minTimezone = timezone;
        } else {
          const currentUTCTime = dayjs();
          const currentTimezoneOffset = currentUTCTime.tz(timezone).utcOffset();
          const currentMinTimezoneOffset = currentUTCTime
            .tz(minTimezone)
            .utcOffset();

          if (currentTimezoneOffset < currentMinTimezoneOffset) {
            minTimezone = timezone;
          }
        }
      } catch (error) {
        // console.log('error', error);
      }
    }
  });

  return minTimezone;
};

/**
 *
 * @param element {HTMLElement}
 */
export async function setMathJaxTypeset(element) {
  if (!element) {
    return log(
      'typesetting MathJax without element is not allowed for performance reasons',
    );
  }
  await window.MathJax?.typesetPromise?.([element]);
}

export function typeSetOnLearnosityItem(ref) {
  const element = document.querySelector(
    `.learnosity-item[data-reference="${ref}"]`,
  );
  element && setMathJaxTypeset(element);
}

export function preloadMathJaxWithLearnosity() {
  if (window.MathJax) {
    return;
  }
  // we need to make a LearnosityApp available for the MathJax, it would be loaded anyway with ItemsApi and has very small bundle size,
  // so we would do it earlier to make sure it is available when we try to load Learnosity MathJax with it "LearnosityApp.renderMath();"
  const questionsApiScript = document.createElement('script');
  questionsApiScript.src = 'https://questions-va.learnosity.com/?v2023.2.LTS';
  document.body.appendChild(questionsApiScript);
  questionsApiScript.onload = () => {
    // for some reasons calling here LearnosityApp.renderMath() does not return Promise here as per documentation
    window.LearnosityApp?.renderMath();
  };
}

// Given that the bugfix inside MathJax itself was postponed for v4 --> https://github.com/mathjax/MathJax/issues/2892
// and Learnosity doesn't give any way to add config values to MathJax intialization,
// this might the only way to add things to the MathJax configuration.
// If you have any advice or other solution please write it in this ticket:
// https://academicmerit.atlassian.net/browse/FY-35798
export function overrideMathJaxConfiguration() {
  let cancelToken;
  const check = () => {
    if (window.MathJax) {
      window.MathJax = merge(window.MathJax, {
        startup: {
          ready() {
            const { FindMathML } = window.MathJax._.input.mathml.FindMathML;
            class MyFindMathML extends FindMathML {
              processMath(set) {
                const { adaptor } = this;
                const math = [];
                // eslint-disable-next-line no-unused-vars
                for (const mml of set.values()) {
                  if (
                    adaptor.kind(adaptor.parent(mml)) === 'mjx-assistive-mml'
                  ) {
                    // eslint-disable-next-line no-continue
                    continue;
                  }
                  const display =
                    adaptor.getAttribute(mml, 'display') === 'block' ||
                    adaptor.getAttribute(mml, 'mode') === 'display';
                  const start = { node: mml, n: 0, delim: '' };
                  const end = { node: mml, n: 0, delim: '' };
                  math.push({
                    math: adaptor.outerHTML(mml),
                    start,
                    end,
                    display,
                  });
                }
                return math;
              }
            }
            window.MathJax = merge(window.MathJax, {
              config: {
                mml: {
                  FindMathML: new MyFindMathML(),
                },
              },
            });
            return window.MathJax.startup.defaultReady();
          },
        },
      });
      cancelAnimationFrame(cancelToken);
    } else {
      cancelToken = requestAnimationFrame(check);
    }
  };
  cancelToken = requestAnimationFrame(check);
  return cancelToken;
}

/**
 *
 * @param {*} fullName
 * @returns Display lastname and then the initial of the first name
 */
export const formatName = (fullName) => {
  const fullNameSplitted = fullName.split(' ');
  if (fullNameSplitted.length < 2) {
    return fullName;
  }
  const lastName = fullNameSplitted[1];
  const firstName = fullNameSplitted[0][0];
  return `${lastName}, ${firstName}`;
};

/**
 *
 * @param {*} subjectId
 * @returns If the subject is not in this mapping is because is a standard subject.
 * It means, it should display Unit, Topic and Skill. Otherwise the tag categories to be displayed should
 * be returned. This is extracted from https://academicmerit.atlassian.net/browse/FY-27071
 */

export const BIG_IDEAS = 'Big Ideas';
export const COURSE_TRANSFER_SKILLS = 'Course Transfer Skills';
export const PROFICIENCIES = 'Proficiencies';
export const UNITS = 'Units';
export const TOPICS = 'Topics';
export const TASK_MODELS = 'Task Models';
export const SKILLS = 'Skills';

export const tagsPerSubject = (masterSubjectId) => {
  const tagsPerSubjectMapping = {
    3: [],
    4: [],
    5: [],
    12: [UNITS, SKILLS],
    13: [UNITS, SKILLS],
    16: [UNITS, TASK_MODELS, SKILLS],
    19: [UNITS, TASK_MODELS, SKILLS],
    23: [UNITS, SKILLS],
    31: [UNITS, TASK_MODELS, SKILLS],
    32: [UNITS, SKILLS],
    86: [UNITS, TASK_MODELS, SKILLS],
    87: [UNITS, TASK_MODELS, SKILLS],
    88: [UNITS, TASK_MODELS, SKILLS],
    94: [BIG_IDEAS, PROFICIENCIES, COURSE_TRANSFER_SKILLS],
    95: [],
    103: [BIG_IDEAS, TOPICS, SKILLS],
  };
  if (!Object.keys(tagsPerSubjectMapping).includes(masterSubjectId)) {
    return [UNITS, TOPICS, SKILLS]; // Return standart Unit, Topic and Skills tags
  }
  return tagsPerSubjectMapping[masterSubjectId];
};
